import React, { useEffect, useState } from "react";
import CssBaseline from "@material-ui/core/CssBaseline";
import { GAME_STAGE } from "../../utils/constant";
import { history } from "../../index";
import { useTheme } from "@material-ui/core";
import { GameModalLayout, CategoryButton, CustomLabel } from "../common";
import { GridList, GridListTile } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Typography } from "@material-ui/core";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import ArrowRightAltOutlinedIcon from "@material-ui/icons/ArrowRightAltOutlined";
import { checkGameStatus } from "../../service/logic";
import { fetchInvestigations } from "../../service/logic/investigationLogic";
import { addGameActivity } from "../../redux/dispatchers";
import { AnswerLinkPopup } from "../common/answer-link-popup";
import { getOptions } from "../../service/api/option";
import { investTypes } from "../../constants/investigation";
import { v4 as uuidv4 } from "uuid";
import { useInvestigationStyles } from "../../styles/investigation-style";
import { Markup } from "interweave";

const Investigation = () => {
  const classes = useInvestigationStyles();
  const theme = useTheme();
  const isWidthSm = useMediaQuery(theme.breakpoints.down("xs"));

  // stage: 0 - select type, 1 - select category, 2 - select subcategory, 3 - select test
  const [stage, setStage] = useState(0);
  // activeType is the currently active investigation type / left tab
  const [activeType, setActiveType] = useState({ id: "", name: "" });
  // activeCategory is the currently active investigation category
  const [activeCategory, setActiveCategory] = useState({ id: "", name: "" });
  // active subcategory is the currently active investigation subcategory (if exists)
  const [activeSubcategory, setActiveSubcategory] = useState({
    id: "",
    name: "",
  });
  // active investigation test
  const [activeTest, setActiveTest] = useState({ id: "", name: "" });
  // investigation categories
  const [categories, setCategories] = useState([]);
  // investigation tests
  const [tests, setTests] = useState([]);
  // filter text
  const [filterText, setFilterText] = useState("");
  // answer
  const [answer, setAnswer] = useState("");
  // link
  const [link, setLink] = useState([]);
  // labels
  const [labels, setLabels] = useState([]);

  // function to display answer
  const fetchAnswer = (content) => {
    const { id, answer, defaultAnswer } = content;
    // save the asked question in user activity
    addGameActivity("investigation", id);
    setAnswer(
      !!answer.link && answer.link.length > 0
        ? answer.contents || ""
        : answer.contents ||
            defaultAnswer ||
            "The test you have chosen is irrelevant."
    );
    setLink(answer.link);
  };

  // when active selections have changed, load labels
  useEffect(() => {
    const tmp = [];
    if (!!activeType.id) {
      tmp.push({
        id: activeType.id,
        name: activeType.name,
        onClick: () => reset(1),
      });
    }
    if (!!activeCategory.id) {
      tmp.push({
        id: activeCategory.id,
        name: activeCategory.name,
        onClick: () => reset(2),
      });
    }
    if (!!activeSubcategory.id) {
      tmp.push({
        id: activeSubcategory.id,
        name: activeSubcategory.name,
        onClick: () => reset(3),
      });
    }
    if (!!activeTest.id) {
      tmp.push({
        id: activeTest.id,
        name: activeTest.name,
        onClick: () => reset(4),
      });
    }
    setLabels(tmp);
  }, [activeType, activeCategory, activeSubcategory, activeTest]);

  const categoryOnClick = (content) => {
    // move to next step on click
    const selected = categories.find((c) => c.id === content.id);
    if (stage === 1) {
      if (!selected.subcategories || selected.subcategories.length === 0) {
        setStage(3);
        setActiveCategory(content);
        setActiveSubcategory({ id: "", name: "" });
        fetchInvestigations(activeType.id, content.optionId).then((tests) => {
          setTests(tests);
        });
      } else {
        setStage(2);
        setActiveCategory(content);
      }
    } else {
      setStage(3);
      setActiveSubcategory(content);
      fetchInvestigations(activeType.id, content.optionId).then((tests) =>
        setTests(tests)
      );
    }
  };

  // render investigation categories / subcategories
  const renderInvestigationCategories = () => {
    // check if stage is select category / select subcategory
    let selected = categories.find((c) => c.id === activeCategory.id);
    const curr =
      stage === 1 ? categories : !!selected ? selected.subcategories : [];
    if (isWidthSm) {
      return (
        <List className={classes.rightList} id="investigationCategory">
          {curr
            .filter((item) => !!item && item.type === activeType.id)
            .map((content) => (
              <ListItem key={content.id} className={classes.listItem}>
                <CategoryButton
                  mobile
                  label={content.name}
                  onClick={() => categoryOnClick(content)}
                />
              </ListItem>
            ))}
        </List>
      );
    }

    return (
      <GridList
        id="investigationCategory"
        spacing={20}
        cellHeight={100}
        cols={2}
        className={classes.rightGridList}
      >
        {curr
          .filter((item) => !!item && item.type === activeType.id)
          .map((content) => (
            <GridListTile key={content.id}>
              <CategoryButton
                label={content.name}
                onClick={() => categoryOnClick(content)}
              />
            </GridListTile>
          ))}
      </GridList>
    );
  };

  // render investigation tests
  const renderTests = () => {
    if (isWidthSm) {
      return (
        <List className={classes.rightList} id="investigation">
          {tests
            .filter(
              (item) =>
                !!item && item.test.search(new RegExp(filterText, "i")) !== -1
            )
            .map((content) => (
              <ListItem key={content.id} className={classes.listItem}>
                <CategoryButton
                  mobile
                  label={content.test}
                  onClick={() => {
                    setStage(4);
                    setActiveTest({
                      id: content.id,
                      name: content.test,
                    });
                    fetchAnswer(content);
                  }}
                />
              </ListItem>
            ))}
        </List>
      );
    }

    return (
      <GridList
        spacing={20}
        cellHeight={100}
        cols={2}
        className={classes.gridListSmaller}
        id="investigation"
      >
        {tests
          .filter(
            (item) =>
              !!item && item.test.search(new RegExp(filterText, "i")) !== -1
          )
          .map((content) => (
            <GridListTile key={content.id}>
              <CategoryButton
                label={content.test}
                onClick={() => {
                  setStage(4);
                  setActiveTest({
                    id: content.id,
                    name: content.test,
                  });
                  fetchAnswer(content);
                }}
              />
            </GridListTile>
          ))}
      </GridList>
    );
  };

  // helper function to identify investigations with subcategories
  const investigationsToLocalInvestigations = (cat) => {
    const tmp = cat.filter((s) => s.dependent === "");
    // find all the subcategories
    tmp.forEach((x) => {
      x.subcategories = cat
        .filter((s) => s.dependent === x.id)
        .map((sub) => ({
          id: uuidv4(),
          optionId: sub.id,
          name: sub.name,
          type: sub.type,
        }));
    });
    // reformat all the categories
    return tmp.map((t) => ({
      id: uuidv4(),
      optionId: t.id,
      name: t.name,
      subcategories: t.subcategories,
      type: t.type,
    }));
  };

  useEffect(() => {
    // check if user is allowed to enter page
    if (!checkGameStatus(GAME_STAGE.INVESTIGATION)) {
      // if not allowed, navigate to case list page
      history.push("/cases");
    }
    // get investigation categories
    getOptions("investCategory").then((opts) => {
      setCategories(investigationsToLocalInvestigations(opts));
    });
  }, []);

  const renderRightContent = () => {
    return (
      <>
        {stage === 0 ? (
          <>
            <Typography variant="h2">Select type of investigation</Typography>
          </>
        ) : stage === 1 || stage === 2 ? (
          <>
            <Typography variant="h2">
              Select {stage === 1 ? "Category" : "Subcategory"}
            </Typography>
            <div className={classes.gridListContainer}>
              {renderInvestigationCategories()}
            </div>
          </>
        ) : stage === 3 ? (
          <>
            <Typography variant="h2">Select Test</Typography>
            <div className={classes.inputContainer}>
              <TextField
                placeholder="Search test"
                variant="outlined"
                size="small"
                className={classes.input}
                onChange={(event) => {
                  setFilterText(event.target.value);
                }}
              />
              <IconButton>
                <ArrowRightAltOutlinedIcon fontSize="small" />
              </IconButton>
            </div>
            <div className={classes.gridListContainer}>{renderTests()}</div>
          </>
        ) : (
          <>
            <Typography variant="h2">Result</Typography>
            <div className={classes.answer} id="answer">
              <Markup content={answer} />
              <AnswerLinkPopup linkArray={link} />
            </div>
          </>
        )}
      </>
    );
  };

  const renderLeftContent = () => {
    return (
      <>
        {isWidthSm ? (
          stage === 0 ? (
            <Typography variant="h4" className={classes.title}>
              Select type of investigation
            </Typography>
          ) : null
        ) : null}
        <List className={classes.leftList} id="investigationList">
          {investTypes.map((content) => (
            <ListItem key={content.id} className={classes.listItem}>
              <CategoryButton
                mobile={isWidthSm}
                label={content.name}
                onClick={() => {
                  if (content.id === activeType.id) {
                    setStage(0);
                    setActiveType({ id: "", name: "" });
                  } else {
                    setStage(1);
                    setActiveType(content);
                  }
                  setActiveCategory({ id: "", name: "" });
                  setActiveSubcategory({ id: "", name: "" });
                  setActiveTest({ id: "", name: "" });
                }}
                red={activeType.id === content.id}
                blue={activeType.id !== content.id}
              />
            </ListItem>
          ))}
        </List>
      </>
    );
  };

  const reset = (stage) => {
    if (stage <= 1) setActiveType({ id: "", name: "" });
    if (stage <= 2) setActiveCategory({ id: "", name: "" });
    if (stage <= 3) setActiveSubcategory({ id: "", name: "" });
    if (stage <= 4) setActiveTest({ id: "", name: "" });
    setStage(stage - 1);
  };

  return (
    <React.Fragment>
      <CssBaseline />
      <GameModalLayout
        title={isWidthSm && stage > 0 ? null : "Investigation"}
        left={
          <div style={{ flex: 9 }}>
            {!isWidthSm || stage === 0 ? (
              renderLeftContent()
            ) : (
              <>
                <CustomLabel arr={labels} />
                {renderRightContent()}
              </>
            )}
          </div>
        }
        right={
          isWidthSm ? null : (
            <>
              <CustomLabel arr={labels} />
              {renderRightContent()}
            </>
          )
        }
      />
    </React.Fragment>
  );
};

export default Investigation;
